import React, { createContext, useContext, useState, useCallback } from 'react';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

interface Alert {
  id: string;
  type: AlertType;
  message: string;
  autoDismiss?: boolean;
  duration?: number;
}

interface AlertContextType {
  alerts: Alert[];
  showAlert: (type: AlertType, message: string, autoDismiss?: boolean, duration?: number) => void;
  dismissAlert: (id: string) => void;
  clearAlerts: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: React.ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const showAlert = useCallback(
    (type: AlertType, message: string, autoDismiss = true, duration = 5000) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newAlert: Alert = { id, type, message, autoDismiss, duration };

      setAlerts((prev) => [...prev, newAlert]);

      if (autoDismiss) {
        setTimeout(() => {
          setAlerts((prev) => prev.filter((alert) => alert.id !== id));
        }, duration);
      }
    },
    []
  );

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const value: AlertContextType = {
    alerts,
    showAlert,
    dismissAlert,
    clearAlerts,
  };

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
};

export default AlertContext;
